import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Receipt, Calculator, CircleDollarSign,
  Sparkles, LogOut, Building2, FileSearch, FileBarChart2, Plug, CreditCard, Settings as SettingsIcon, Users, MessageSquare, Briefcase, TrendingUp, Landmark,
} from "lucide-react";

const ITEMS = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, testid: "nav-overview", end: true },
  { to: "/app/firm", label: "Firm Dashboard", icon: TrendingUp, testid: "nav-firm" },
  { to: "/app/accounts", label: "Bank accounts", icon: Landmark, testid: "nav-bank-accounts" },
  { to: "/app/transactions", label: "Transactions", icon: Receipt, testid: "nav-transactions" },
  { to: "/app/statements", label: "Statements", icon: FileSearch, testid: "nav-statements" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart2, testid: "nav-reports" },
  { to: "/app/tax", label: "Tax Savings", icon: Calculator, testid: "nav-tax" },
  { to: "/app/debts", label: "Debt", icon: CircleDollarSign, testid: "nav-debts" },
  { to: "/app/advisor", label: "AI Advisor", icon: Sparkles, testid: "nav-advisor" },
  { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/app/back-office", label: "Back Office", icon: Briefcase, testid: "nav-back-office", badgeKey: "backoffice" },
  { to: "/app/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", badgeKey: "messages" },
  { to: "/app/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [openTasks, setOpenTasks] = useState(0);
  const [overdueTasks, setOverdueTasks] = useState(0);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [m, s] = await Promise.all([
          api.get("/messages/unread-count"),
          api.get("/back-office/summary"),
        ]);
        if (!alive) return;
        setUnread(m.data.total_unread || 0);
        setOpenTasks(s.data.open_count || 0);
        setOverdueTasks(s.data.overdue_count || 0);
      } catch { /* noop */ }
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-black bg-black text-white" data-testid="sidebar">
      <div className="px-6 py-7 border-b border-white/10">
        <Link to="/app" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <div className="w-8 h-8 rounded-md bg-forest flex items-center justify-center text-white">
            <Building2 className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <span className="font-heading text-xl font-semibold tracking-tight text-white">BizCashflow <span className="text-forest">Pros</span></span>
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-0.5">
        {ITEMS.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-white/10 text-white font-medium"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <it.icon className="w-4 h-4" strokeWidth={1.75} />
            <span className="flex-1">{it.label}</span>
            {it.badgeKey === "messages" && unread > 0 && (
              <span
                className="text-[10px] font-medium bg-forest text-white rounded-full px-1.5 py-0.5"
                data-testid="nav-messages-badge"
              >
                {unread}
              </span>
            )}
            {it.badgeKey === "backoffice" && openTasks > 0 && (
              <span
                className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${
                  overdueTasks > 0 ? "bg-ochre text-white" : "bg-forest text-white"
                }`}
                data-testid="nav-back-office-badge"
              >
                {openTasks}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2.5 mb-2">
          <div className="text-sm font-medium text-white truncate" data-testid="sidebar-username">{user?.name}</div>
          <div className="text-xs text-white/50 truncate">{user?.business_name || user?.email}</div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.75} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
